<template>
    <transition name="slide-up">
        <div
            v-if="visible"
            ref="modal"
            class="custom-modal"
            :class="modalClass"
            :style="modalStyle"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="ariaLabelledby || undefined"
            :aria-label="ariaLabel || undefined"
            tabindex="-1"
            @keydown="handleKeydown"
        >
            <button
                type="button"
                class="close-btn icon-btn icon-btn-white"
                :aria-label="resolvedCloseLabel"
                @click="closeModal"
            >
                <i class="fi fi-x" aria-hidden="true"></i>
            </button>

            <slot></slot>
        </div>
    </transition>

    <!-- Overlay div to close the modal when clicking outside -->
    <div
        v-if="visible"
        class="overlay"
        aria-hidden="true"
        @click="closeModal"
    ></div>
</template>

<script>
export default {
    name: 'Modal',
    props: {
        // toggles modal visibility
        visible: {
            type: Boolean,
            required: true,
        },
        modalClass: {
            type: [String, Array, Object],
            default: '',
        },
        modalStyle: {
            type: [String, Object, Array],
            default: '',
        },
        ariaLabelledby: {
            type: String,
            default: null,
        },
        ariaLabel: {
            type: String,
            default: null,
        },
        closeLabel: {
            type: String,
            default: null,
        },
    },
    emits: ['close'],
    data() {
        return {
            previouslyFocusedElement: null,
        };
    },
    computed: {
        resolvedCloseLabel() {
            if (this.closeLabel) return this.closeLabel;
            if (typeof this.$t === 'function') {
                return this.$t('common.actions.close');
            }
            return 'Close';
        },
    },
    watch: {
        visible(value) {
            if (value) {
                this.focusDialog();
            } else {
                this.restoreFocus();
            }
        },
    },
    mounted() {
        if (this.visible) this.focusDialog();
    },
    beforeUnmount() {
        this.restoreFocus();
    },
    methods: {
        closeModal() {
            this.$emit('close');
        },
        focusDialog() {
            this.previouslyFocusedElement = document.activeElement;
            this.$nextTick(() => {
                if (!this.visible || !this.$refs.modal) return;
                const focusableElements = this.getFocusableElements();
                (focusableElements[0] || this.$refs.modal).focus();
            });
        },
        restoreFocus() {
            const trigger = this.previouslyFocusedElement;
            this.previouslyFocusedElement = null;
            if (trigger && document.contains(trigger)) trigger.focus();
        },
        getFocusableElements() {
            if (!this.$refs.modal) return [];
            const selector = [
                'a[href]',
                'area[href]',
                'button:not([disabled])',
                'input:not([disabled]):not([type="hidden"])',
                'select:not([disabled])',
                'textarea:not([disabled])',
                'iframe',
                'object',
                'embed',
                '[contenteditable="true"]',
                '[tabindex]:not([tabindex="-1"])',
            ].join(',');
            return Array.from(
                this.$refs.modal.querySelectorAll(selector)
            ).filter(
                (element) =>
                    element.getAttribute('aria-hidden') !== 'true' &&
                    element.getClientRects().length > 0
            );
        },
        handleKeydown(event) {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                this.closeModal();
                return;
            }
            if (event.key !== 'Tab') return;

            const focusableElements = this.getFocusableElements();
            if (!focusableElements.length) {
                event.preventDefault();
                this.$refs.modal.focus();
                return;
            }

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            if (
                event.shiftKey &&
                (document.activeElement === firstElement ||
                    document.activeElement === this.$refs.modal)
            ) {
                event.preventDefault();
                lastElement.focus();
            } else if (
                !event.shiftKey &&
                document.activeElement === lastElement
            ) {
                event.preventDefault();
                firstElement.focus();
            }
        },
    },
};
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
    transition: all 0.3s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
    opacity: 0;
    transform: translateY(20px);
}

.custom-modal {
    background-color: white;
    z-index: var(--z-index-modal);
    width: 500px;
    max-width: 95%;
    height: fit-content;
    position: fixed; /* Ensures the modal is positioned relative to the viewport */
    top: 50%; /* Centers the modal vertically */
    left: 50%; /* Centers the modal horizontally */
    transform: translate(-50%, -50%); /* Adjusts for the modal's size */
    border-radius: var(--radius-default);
    padding: var(--space-10) 3rem;
}

.overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    /* semi-transparent overlay */
    background-color: rgba(0, 0, 0, 0.1);
    /* one level below the modal */
    z-index: var(--z-index-modal-overlay);
}

.close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
}

@media (max-width: 767.98px) {
    .custom-modal {
        width: 320px;
        padding: var(--space-4) 1rem;
    }
}
</style>
